import { createSlice } from "@reduxjs/toolkit";

// Initial state containing default styles for all codeblock types
const initialState = {
  container: {
    background: "#FFFFFF",
    height: 10,
    radius: 8,
    fullWidth: false,
    spacing: {
      top: 5,
      right: 0,
      bottom: 5,
      left: 0,
    },
    margin: {
      top: 5,
      right: 10,
      bottom: 5,
      left: 10,
    },
    isApplyPaddingOnAllSides: false,
    isApplyMarginOnAllSides: false,
    allSideSpacing: 5,
    allSideMargin: 10,
    opacity: 100,
    border: {
      color: null,
      width: 1,
    },
    dropShadowColor: null,
    isFirstContainer: true,
  },

  button: {
    buttonProps: {
      isActive: false,
      radius: null,
      backgroundColor: null,
      opacity: null,
      border: { color: null, width: null },
      dropShadowColor: "#868E96",
    },
  },

  divider: {
    width: 3,
    color: "#000000",
    style: "solid",
    margin: {
      top: 10,
      bottom: 10,
      left: 10,
      right: 10,
    },
    isApplyMarginOnAllSides: false,
    allSideMargin: 10,
    opacity: 0,
  },

  image: {
    margin: {
      top: 5,
      bottom: 5,
      left: 10,
      right: 10,
    },
    isApplyMarginOnAllSides: false,
    allSideMargin: 5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: null,
    dropShadowColor: null,
    alignment: "center",
    showImageTitle: false,
    hyperlink: null,
    showHyperlink: false,
    width: "100%",
  },

  video: {
    margin: {
      top: 5,
      bottom: 5,
      left: 10,
      right: 10,
    },
    isApplyMarginOnAllSides: false,
    allSideMargin: 5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: null,
    dropShadowColor: null,
    showVideoTitle: false,
    hyperlink: null,
    showHyperlink: false,
    width: "100%",
    title: "Untitled Video",
  },

  audio: {
    audios: [],
    textColor: "",
    backgroundColor: "suggested_color_0",
    waveColor: "suggested_color_0",
    iconColor: "suggested_color_0",
    margin: {
      top: 5,
      bottom: 5,
      left: 5,
      right: 5,
    },
    isApplyMarginOnAllSides: false,
    allSideMargin: 5,
  },

  document: {
    documents: [],
    margin: {
      top: 5,
      bottom: 5,
      left: 10,
      right: 10,
    },
    isApplyMarginOnAllSides: false,
    allSideMargin: 10,
    columns: 3,
    showThumbnail: false,
  },

  imageGrid: {
    margin: {
      top: 5,
      bottom: 5,
      left: 10,
      right: 10,
    },
    isApplyMarginOnAllSides: false,
    allSideMargin: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: null,
    dropShadowColor: null,
    alignment: "center",
    showCaptions: false,
    gridType: "standard",
    columns: 3,
  },

  accordion: {
    header: "Accordion Header",
    expanded: false,
    margin: {
      top: 5,
      bottom: 5,
      left: 10,
      right: 10,
    },
    isApplyMarginOnAllSides: false,
    allSideMargin: 5,
    borderColor: null,
    borderWidth: 1,
    borderRadius: 8,
    headerBgColor: null,
    headerTextColor: "#FFFFFF",
    contentBgColor: "#FFFFFF",
    contentTextColor: "#000",
    selectedBoxSizeId: 1,
    dropDownRightIcon: 0,
    dropDownIconColor: "#FFFFFF",
    headerOpacity: 100,
    contentOpacity: 100,
    dropShadowColor: null,
    selectedIcon: null,
  },

  text: {
    text: "",
    fontSize: 16,
    padding: {
      top: 5,
      bottom: 5,
      left: 10,
      right: 10,
    },
    allSidePadding: 5,
    isApplyPaddingOnAllSides: false,
    headingProps: {
      fontColor: "black",
      fontFamily: "Poppins",
      fontSize: 18,
      resetKey: 0,
    },
    paragraphProps: {
      fontColor: "black",
      fontFamily: "Poppins",
      fontSize: 14,
      resetKey: 0,
    },
    lineHeight: 1.25,
  },

  imageWithText: {
    image: {
      src: "",
      title: "",
    },
    alt: "Image with text",
    defaultImage: "",
    margin: {
      top: 5,
      bottom: 5,
      left: 10,
      right: 10,
    },
    isApplyMarginOnAllSides: false,
    allSideMargin: 5,
    width: "50%",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "suggested_color_0",
    showImageTitle: false,
    hyperlink: null,
    showHyperLink: false,
    dropShadowColor: null,
    textPosition: "right",
    text: "",
    headingProps: {
      fontColor: "black",
      fontFamily: "Poppins",
      fontSize: 18,
      resetKey: 0,
    },
    paragraphProps: {
      fontColor: "black",
      fontFamily: "Poppins",
      fontSize: 14,
      resetKey: 0,
    },
    lineHeight: 1.25,
  },

  linkWheel: {
    wheelId: "",
    showDescription: false,
    margin: {
      top: 5,
      bottom: 5,
      left: 10,
      right: 10,
    },
    isApplyMarginOnAllSides: false,
    allSideMargin: 5,
    borderRadius: 8,
    titleColor: "#123051",
    textColor: "#565556",
    backgroundColor: "#E5ECFA",
    backgroundOpacity: 100,
    borderColor: "suggested_color_0",
    borderWidth: 1,
    dropShadowColor: null,
  },

  socialMediaLinks: {
    socialLinks: [],
    alignment: "center",
    iconsPerRow: 4,
    margin: {
      top: 5,
      bottom: 5,
      left: 10,
      right: 10,
    },
    isApplyMarginOnAllSides: false,
    allSideMargin: 5,
  },

  // Add other codeblock types in the future as needed
};

const codeblockDefaultStylesSlice = createSlice({
  name: "codeblockDefaultStyles",
  initialState,
  reducers: {
    updateContainerStyles: (state, action) => {
      state.container = action.payload;
    },
    updateDividerStyles: (state, action) => {
      state.divider = action.payload;
    },
    updateImageStyles: (state, action) => {
      state.image = action.payload;
    },
    updateVideoStyles: (state, action) => {
      state.video = action.payload;
    },
    updateAudioStyles: (state, action) => {
      state.audio = action.payload;
    },
    updateDocumentStyles: (state, action) => {
      state.document = action.payload;
    },
    updateImageGridStyles: (state, action) => {
      state.imageGrid = action.payload;
    },
    updateAccordionStyles: (state, action) => {
      state.accordion = action.payload;
    },
    updateTextStyles: (state, action) => {
      state.text = action.payload;
    },
    updateImageWithTextStyles: (state, action) => {
      state.imageWithText = action.payload;
    },
    updateLinkWheelStyles: (state, action) => {
      state.linkWheel = action.payload;
    },
    updateSocialMediaLinksStyles: (state, action) => {
      state.socialMediaLinks = action.payload;
    },
    updateButtonProps: (state, action) => {
      state.button.buttonProps = {
        ...state.button.buttonProps,
        ...action.payload,
      };
    },
    resetContainerStyles: (state) => {
      state.container = initialState.container;
    },
    resetDividerStyles: (state) => {
      state.divider = initialState.divider;
    },
    resetImageStyles: (state) => {
      state.image = initialState.image;
    },
    resetVideoStyles: (state) => {
      state.video = initialState.video;
    },
    resetAudioStyles: (state) => {
      state.audio = initialState.audio;
    },
    resetDocumentStyles: (state) => {
      state.document = initialState.document;
    },
    resetImageGridStyles: (state) => {
      state.imageGrid = initialState.imageGrid;
    },
    resetAccordionStyles: (state) => {
      state.accordion = initialState.accordion;
    },
    resetTextStyles: (state) => {
      state.text = initialState.text;
    },
    resetImageWithTextStyles: (state) => {
      state.imageWithText = initialState.imageWithText;
    },
    resetLinkWheelStyles: (state) => {
      state.linkWheel = initialState.linkWheel;
    },
    resetSocialMediaLinksStyles: (state) => {
      state.socialMediaLinks = initialState.socialMediaLinks;
    },
    resetButtonProps: (state) => {
      state.button.buttonProps = initialState.button.buttonProps;
    },
    // Add other actions as needed
  },
});

export const {
  updateContainerStyles,
  updateDividerStyles,
  updateImageStyles,
  updateVideoStyles,
  updateAudioStyles,
  updateDocumentStyles,
  updateImageGridStyles,
  updateAccordionStyles,
  updateTextStyles,
  updateImageWithTextStyles,
  updateLinkWheelStyles,
  updateSocialMediaLinksStyles,
  updateButtonProps,
  resetContainerStyles,
  resetDividerStyles,
  resetImageStyles,
  resetVideoStyles,
  resetAudioStyles,
  resetDocumentStyles,
  resetImageGridStyles,
  resetAccordionStyles,
  resetTextStyles,
  resetImageWithTextStyles,
  resetLinkWheelStyles,
  resetSocialMediaLinksStyles,
  resetButtonProps,
} = codeblockDefaultStylesSlice.actions;

export default codeblockDefaultStylesSlice.reducer;
